/**
 * WeaveIn.ai Platform - Enrollments API Service
 * TypeScript service layer for enrollment management
 */

import axios, { AxiosInstance } from 'axios';
import { initializeActiveCboId } from '../../utils/auth';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type EnrollmentStatus =
  | 'pending'
  | 'active'
  | 'on_hold'
  | 'completed'
  | 'withdrawn'
  | 'terminated';

export type ActivityType =
  | 'outreach'
  | 'assessment'
  | 'care_plan'
  | 'follow_up'
  | 'case_management'
  | 'referral'
  | 'other';

export type ActivityStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';

export interface EnrollmentBase {
  member_id: number;
  program_id: number;
  status?: EnrollmentStatus;
  enrollment_date?: string;
  start_date?: string;
  end_date?: string;
  assigned_case_manager_id?: number;
  priority_level?: string;
  eligibility_verified?: boolean;
  consent_obtained?: boolean;
  notes?: string;
}

export interface EnrollmentCreate extends EnrollmentBase {
  member_id: number;
  program_id: number;
}

export interface Enrollment extends EnrollmentBase {
  id: number;
  member_id: number;
  program_id: number;
  cbo_id: number;
  enrollment_status: EnrollmentStatus; // Backend field name
  enrollment_priority?: string; // Backend field name
  assigned_to?: number; // Backend field name
  progress_percentage?: number;
  completed_activities?: number;
  total_required_activities?: number;
  completion_date?: string;
  last_contact_date?: string;
  outcome_status?: string;
  created_ts: string;
  updated_ts: string; // Backend field name
  created_by?: number;
  updated_by?: number;
  // Joined data
  member_name?: string;
  program_name?: string;
  assigned_to_name?: string; // Backend field name for case manager
  outstanding_tasks_count?: number;
  unread_conversations_count?: number;
}

export interface EnrollmentListResponse {
  enrollments: Enrollment[];
  total: number;
  page: number;
  page_size: number;
}

export interface ActivityBase {
  activity_type: ActivityType;
  activity_date: string;
  status?: ActivityStatus;
  duration_minutes?: number;
  location?: string;
  notes?: string;
  performed_by_user_id?: number;
  billable?: boolean;
  billing_code?: string;
  units?: number;
}

export interface ActivityCreate extends ActivityBase {
  enrollment_id: number;
}

/** The fields an author or admin may change on an activity already logged (TRUS-509). */
export interface ActivityUpdate {
  activity_type?: ActivityType;
  activity_name?: string;
  activity_date?: string;
  duration_minutes?: number;
  location?: string;
  completion_notes?: string;
}

export interface Activity extends ActivityBase {
  id: number;
  enrollment_id: number;
  cbo_id: number;
  status: ActivityStatus;
  created_ts: string;
  last_updated_ts: string;
  performed_by_name?: string;
}

export interface DocumentBase {
  document_type: string;
  file_name: string;
  file_path: string;
  file_url?: string;
  file_size?: number;
  mime_type?: string;
  description?: string;
  uploaded_by_user_id?: number;
}

export interface DocumentCreate extends DocumentBase {
  enrollment_id: number;
}

export interface Document extends DocumentBase {
  id: number;
  enrollment_id: number;
  cbo_id: number;
  document_name?: string;
  upload_date: string;
  uploaded_by_name?: string;
  // Free-text notes captured on the Upload Document dialog (TRUS-448).
  notes?: string | null;
}

export interface NoteBase {
  note_text: string;
  note_type?: string;
  is_confidential?: boolean;
  created_by_user_id?: number;
}

export interface NoteCreate extends NoteBase {
  enrollment_id: number;
}

export interface Note extends NoteBase {
  id: number;
  enrollment_id: number;
  cbo_id: number;
  created_ts: string;
  last_updated_ts: string;
  created_by_name?: string;
}

export interface GoalBase {
  goal_text: string;
  goal_category?: string;
  target_date?: string;
  status?: string;
  progress_notes?: string;
  created_by_user_id?: number;
}

export interface GoalCreate extends GoalBase {
  enrollment_id: number;
}

export interface Goal extends GoalBase {
  id: number;
  enrollment_id: number;
  cbo_id: number;
  created_ts: string;
  last_updated_ts: string;
  created_by_name?: string;
}

export interface MemberSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone_no?: string;
  patient_no?: string;
  date_of_birth?: string;
}

export interface MemberSearchResponse {
  members: MemberSearchResult[];
  total: number;
}

export interface EnrollmentProgram {
  id: number;
  name: string;
  program_type?: string;
  is_active: boolean;
}

export interface EnrollmentProgramListResponse {
  programs: EnrollmentProgram[];
}

// ============================================================================
// API CLIENT
// ============================================================================

class EnrollmentsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    // Initialize active CBO ID from JWT token if not already set
    initializeActiveCboId();

    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // ENROLLMENT ENDPOINTS
  // ========================================================================

  async createEnrollment(data: EnrollmentCreate): Promise<Enrollment> {
    const { offlineAwareMutate } = await import('@/lib/offline/hooks');
    const res: any = await offlineAwareMutate('CREATE', 'enrollments', undefined, data, async () => {
      const response = await this.client.post<Enrollment>('/api/enrollments/', data);
      return response.data;
    });
    return res && res.queued ? ({ id: -Date.now(), ...data } as unknown as Enrollment) : res;
  }

  async listEnrollments(params?: {
    page?: number;
    page_size?: number;
    member_id?: number;
    program_id?: number;
    status?: EnrollmentStatus;
    search?: string;
  }): Promise<EnrollmentListResponse> {
    console.log('🌐 EnrollmentsAPI.listEnrollments: Making API call with params:', params);
    console.log('🌐 EnrollmentsAPI.listEnrollments: active_cbo_id =', localStorage.getItem('active_cbo_id'));

    const response = await this.client.get<EnrollmentListResponse>('/api/enrollments/', { params });

    console.log('🌐 EnrollmentsAPI.listEnrollments: Raw response:', response);
    console.log('🌐 EnrollmentsAPI.listEnrollments: Response data:', response.data);

    return response.data;
  }

  async getEnrollment(enrollmentId: number): Promise<Enrollment> {
    const response = await this.client.get<Enrollment>(`/api/enrollments/${enrollmentId}`);
    return response.data;
  }

  async updateEnrollment(enrollmentId: number, data: Partial<EnrollmentBase>): Promise<Enrollment> {
    const { offlineAwareMutate } = await import('@/lib/offline/hooks');
    const res: any = await offlineAwareMutate('UPDATE', 'enrollments', String(enrollmentId), data, async () => {
      const response = await this.client.put<Enrollment>(`/api/enrollments/${enrollmentId}`, data);
      return response.data;
    });
    return res && res.queued ? ({ id: enrollmentId, ...data } as unknown as Enrollment) : res;
  }

  async deleteEnrollment(enrollmentId: number): Promise<void> {
    await this.client.delete(`/api/enrollments/${enrollmentId}`);
  }

  async searchMembers(params: {
    search: string;
    program_id?: number;
    limit?: number;
  }): Promise<MemberSearchResponse> {
    const response = await this.client.get<MemberSearchResponse>('/api/enrollments/search-members', { params });
    return response.data;
  }

  async getProgramsWithEnrollments(): Promise<EnrollmentProgramListResponse> {
    const response = await this.client.get<EnrollmentProgramListResponse>('/api/enrollments/programs-with-enrollments');
    return response.data;
  }

  // ========================================================================
  // ACTIVITY ENDPOINTS
  // ========================================================================

  async createActivity(data: ActivityCreate): Promise<Activity> {
    const { enrollment_id, ...activityData } = data;
    const response = await this.client.post<Activity>(`/api/enrollments/${enrollment_id}/activities`, activityData);
    return response.data;
  }

  async listActivities(enrollmentId: number): Promise<Activity[]> {
    const response = await this.client.get<Activity[]>(`/api/enrollments/${enrollmentId}/activities`);
    return response.data;
  }

  /**
   * Correct a logged activity. Narrower than ActivityCreate on purpose: enrollment,
   * org and billing linkage are fixed once logged, and completion goes through
   * completeActivity so the billing side effects stay in one place. Refused by the
   * backend for anyone but the author or an admin, and once the activity is billed.
   */
  async updateActivity(activityId: number, data: ActivityUpdate): Promise<Activity> {
    const response = await this.client.put<Activity>(`/api/enrollments/activities/${activityId}`, data);
    return response.data;
  }

  /**
   * Mark an activity complete. If billable, the backend auto-generates the billing
   * record (claim + line) and runs the mock clearinghouse. Returns the created claim.
   */
  async completeActivity(
    enrollmentId: number,
    activityId: number,
    completionNotes?: string
  ): Promise<{
    activity_id: number;
    enrollment_id: number;
    is_completed: boolean;
    billing: { claim: { id: number; claim_number: string; claim_status: string; total_charge: number } | null; reason?: string };
  }> {
    const response = await this.client.put(
      `/api/enrollments/${enrollmentId}/activities/${activityId}/complete`,
      { completion_notes: completionNotes ?? null }
    );
    return response.data;
  }

  async deleteActivity(activityId: number): Promise<void> {
    await this.client.delete(`/api/enrollments/activities/${activityId}`);
  }

  // ========================================================================
  // DOCUMENT ENDPOINTS
  // ========================================================================

  async createDocument(data: DocumentCreate): Promise<Document> {
    const { enrollment_id, ...documentData } = data;
    const response = await this.client.post<Document>(`/api/enrollments/${enrollment_id}/documents`, documentData);
    return response.data;
  }

  async listDocuments(enrollmentId: number): Promise<Document[]> {
    const response = await this.client.get<Document[]>(`/api/enrollments/${enrollmentId}/documents`);
    return response.data;
  }

  async deleteDocument(documentId: number): Promise<void> {
    // New doc_records system uses offset IDs (id + 1000000) to avoid collision with legacy
    if (documentId >= 1000000) {
      const realId = documentId - 1000000;
      await this.client.delete(`/api/documents/${realId}`);
    } else {
      // Legacy enrollment_documents — delete via enrollments path
      await this.client.delete(`/api/enrollments/documents/${documentId}`);
    }
  }

  // ========================================================================
  // NOTE ENDPOINTS
  // ========================================================================

  async createNote(data: NoteCreate): Promise<Note> {
    const { enrollment_id, ...noteData } = data;
    const response = await this.client.post<Note>(`/api/enrollments/${enrollment_id}/notes`, noteData);
    return response.data;
  }

  async listNotes(enrollmentId: number): Promise<Note[]> {
    const response = await this.client.get<Note[]>(`/api/enrollments/${enrollmentId}/notes`);
    return response.data;
  }

  async updateNote(noteId: number, data: Partial<NoteBase>): Promise<Note> {
    const response = await this.client.put<Note>(`/api/enrollments/notes/${noteId}`, data);
    return response.data;
  }

  async deleteNote(noteId: number): Promise<void> {
    await this.client.delete(`/api/enrollments/notes/${noteId}`);
  }

  // ========================================================================
  // GOAL ENDPOINTS
  // ========================================================================

  async createGoal(data: GoalCreate): Promise<Goal> {
    const { enrollment_id, ...goalData } = data;
    const response = await this.client.post<Goal>(`/api/enrollments/${enrollment_id}/goals`, goalData);
    return response.data;
  }

  async listGoals(enrollmentId: number): Promise<Goal[]> {
    const response = await this.client.get<Goal[]>(`/api/enrollments/${enrollmentId}/goals`);
    return response.data;
  }

  async updateGoal(goalId: number, data: Partial<GoalBase>): Promise<Goal> {
    const response = await this.client.put<Goal>(`/api/enrollments/goals/${goalId}`, data);
    return response.data;
  }

  async deleteGoal(goalId: number): Promise<void> {
    await this.client.delete(`/api/enrollments/goals/${goalId}`);
  }
}

// Export singleton instance
export const enrollmentsAPI = new EnrollmentsAPI();

// ============================================================================
// REACT HOOKS
// ============================================================================

export { default as useEnrollments } from '@/hooks/useEnrollments';
